import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { MediaItem } from "@/data/media";
import { pillars as codePillars, type Pillar } from "@/data/programs";
import {
  footerNav as staticFooterNav,
  headerButton as staticHeaderButton,
  primaryNav as staticPrimaryNav,
  type NavItem,
} from "@/data/navigation";
import { organisationSettings } from "@/data/organisation";

import type { SettingsDoc, SiteContent } from "./content";

/**
 * Live content shared by every page, provided once per render from the root
 * loader.
 *
 * The split is deliberate. Which photographs appear in which group, and in what
 * order, is layout: it lives in client/src/data where a developer can see it
 * next to the component that renders it. What each photograph *says*, its alt
 * text and caption, is editorial, and staff change it in the admin panel. So
 * the groupings stay imported from code and only the editable fields are
 * overlaid from the database.
 *
 * Every hook below falls back to the committed default. A super admin can take
 * the navigation apart or empty a field, and the page renders what is in
 * client/src/data instead of a hole. That is what makes it safe to hand these
 * controls to somebody who is not a developer.
 *
 * A page's own sections are not here. They arrive with the page; see
 * page-data.tsx.
 */

const ContentContext = createContext<SiteContent | null>(null);

export function ContentProvider({ value, children }: { value: SiteContent; children: ReactNode }) {
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent(): SiteContent {
  const content = useContext(ContentContext);
  if (!content) {
    throw new Error("useContent was called outside ContentProvider. The root loader supplies it.");
  }
  return content;
}

/**
 * Overlays the editable fields of one media item with whatever the database
 * currently holds, matched on id.
 *
 * Falls back to the imported item when there is no row, so a photograph added
 * to a group in code renders correctly before anyone has seeded it. `asset` is
 * never taken from the database in that fallback path, because the file on disk
 * is the thing the id points at.
 */
export function useMedia(): (item: MediaItem) => MediaItem {
  const { media } = useContent();
  return (item) => {
    const live = media[item.id];
    return live ? { ...item, ...live } : item;
  };
}

/** Resolves a media key from the CMS into a whole item, or null if there is none. */
export function useMediaByKey(): (key?: string | null) => MediaItem | null {
  const { media } = useContent();
  return (key) => (key ? (media[key] ?? null) : null);
}

/**
 * The organisation's identity.
 *
 * Never null. Before the settings document exists, or if the API returns
 * without one, this is the committed snapshot the seed was built from, so a
 * page that names the organisation still names it correctly.
 */
export function useSettings(): SettingsDoc {
  const { settings } = useContent();
  return useMemo(() => settings ?? (organisationSettings as unknown as SettingsDoc), [settings]);
}

/**
 * The header menu.
 *
 * Built from the CMS when there is anything there, and from the committed
 * default when there is not. An empty navigation collection is treated as "not
 * seeded" rather than as "the client deleted every menu item", because the
 * second is a decision somebody would make one item at a time.
 */
export function usePrimaryNav(): NavItem[] {
  const { navigation } = useContent();
  return useMemo(() => {
    const rows = navigation.filter((g) => g.location === "primary");
    if (rows.length === 0) return staticPrimaryNav;
    return rows
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((group) => ({
        label: group.heading,
        href: group.href ?? "/",
        overviewLabel: group.overviewLabel,
        children: group.links.filter((link) => !link.hidden).length
          ? group.links
              .filter((link) => !link.hidden)
              .slice()
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((link) => ({
                label: link.label,
                href: link.href,
                description: link.description,
                external: link.external,
              }))
          : undefined,
      }));
  }, [navigation]);
}

/**
 * The button at the right of the header.
 *
 * Stored as the one "utility" navigation row: its heading is how the panel names
 * it, `overviewLabel` is the words on the button, `href` is where it goes. Hidden
 * in the panel, it is left off the header and the mobile menu.
 */
export function useHeaderButton(): { label: string; href: string } | null {
  const { navigation } = useContent();
  return useMemo(() => {
    const rows = navigation.filter((g) => g.location === "utility");
    if (rows.length === 0) return staticHeaderButton;
    const row = rows.slice().sort((a, b) => a.order - b.order)[0];
    if (!row?.href) return null;
    return { label: row.overviewLabel || row.heading, href: row.href };
  }, [navigation]);
}

export function useFooterNav(): { heading: string; links: { label: string; href: string; external?: boolean }[] }[] {
  const { navigation } = useContent();
  return useMemo(() => {
    const rows = navigation.filter((g) => g.location === "footer");
    if (rows.length === 0) {
      return staticFooterNav.map((group) => ({
        heading: group.heading,
        links: group.links.map((link) => ({ label: link.label, href: link.href })),
      }));
    }
    return rows
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((group) => ({
        heading: group.heading,
        links: group.links
          .filter((link) => !link.hidden)
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((link) => ({ label: link.label, href: link.href, external: link.external })),
      }));
  }, [navigation]);
}

/**
 * The four programme pillars, with whatever the CMS says about them overlaid.
 *
 * The slug, and therefore the route, stays code-owned: /our-work/:pillar
 * matches on it and a typo in a text box would 404 a whole programme. The order
 * of the four is code-owned for the same reason. What a card shows (the title,
 * the outcome line, the two example activities, the button text and the
 * photograph) comes from the CMS where there is a row and from the committed
 * content where there is not. What the programme page itself says lives in that
 * page's sections.
 *
 * A field left empty in the panel falls back rather than publishing a blank, so
 * clearing a box cannot produce a programme card with no outcome on it.
 */
export function usePillars(): Pillar[] {
  const { programmes, media } = useContent();

  return useMemo(() => {
    if (!programmes.length) return codePillars;
    const byslug = new Map(programmes.map((p) => [p.slug, p]));

    return codePillars.map((pillar) => {
      const live = byslug.get(pillar.slug);
      if (!live) return pillar;

      const pick = (key: string | undefined, fallback: MediaItem) => (key ? (media[key] ?? fallback) : fallback);
      const examples = (live.examples ?? []).filter(Boolean);

      return {
        ...pillar,
        title: live.title || pillar.title,
        shortTitle: live.shortTitle || pillar.shortTitle,
        outcome: live.outcome || pillar.outcome,
        cta: live.cta || pillar.cta,
        examples: examples.length ? [examples[0], examples[1] ?? ""] : pillar.examples,
        image: pick(live.mediaKey, pillar.image),
        cardImage: live.cardMediaKey ? pick(live.cardMediaKey, pillar.image) : pillar.cardImage,
      } satisfies Pillar;
    });
  }, [programmes, media]);
}

/** One programme by slug, with the CMS overlay applied. */
export function usePillar(slug: string): Pillar | null {
  const pillars = usePillars();
  return useMemo(() => pillars.find((p) => p.slug === slug) ?? null, [pillars, slug]);
}

/** One configurable form by key, or null when it has not been seeded. */
export function useForm(key: string) {
  const { forms } = useContent();
  return useMemo(() => forms.find((f) => f.key === key) ?? null, [forms, key]);
}
